/**
 * Reason Dialog Component
 *
 * Dialog สำหรับใส่เหตุผลเมื่อ Cancel/Reject transaction
 * - บังคับใส่เหตุผล
 * - แสดงข้อมูลสรุปก่อนยืนยัน
 * - รองรับ creditRefund notification
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Loader2, XCircle, Undo2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

// ============================================
// Types
// ============================================

export interface ReasonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "reject" | "cancel";
  transaction: {
    id: string;
    amount: number;
    memberName?: string;
    status: string;
    isCreditWithdraw?: string;
  };
  onConfirm: (reason: string) => Promise<void>;
}

// ============================================
// Preset Reasons
// ============================================

const REJECT_REASONS = [
  "ข้อมูลไม่ถูกต้อง",
  "ยอดเงินไม่ตรง",
  "ลูกค้าขอยกเลิก",
  "ข้อมูลธนาคารไม่ตรง",
  "ติดโปรโมชั่น",
  "เครดิตไม่พอ",
];

const CANCEL_REASONS = [
  "ลูกค้าขอยกเลิก",
  "โอนเงินไม่สำเร็จ",
  "ข้อมูลผิดพลาด",
  "ทำรายการซ้ำ",
  "ระบบธนาคารมีปัญหา",
];

// ============================================
// Main Component
// ============================================

export function ReasonDialog({
  open,
  onOpenChange,
  type,
  transaction,
  onConfirm,
}: ReasonDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const isReject = type === "reject";
  const presetReasons = isReject ? REJECT_REASONS : CANCEL_REASONS;

  // ตรวจสอบว่าต้องคืนเครดิตหรือไม่
  const willRefundCredit =
    type === "cancel" &&
    (transaction.status === "Processing" ||
      transaction.isCreditWithdraw === "1");

  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError(t("smartCashier.reasonRequired", "กรุณาระบุเหตุผล"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await onConfirm(reason.trim());
      setReason("");
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setReason("");
      setError("");
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isReject ? (
              <XCircle className="w-5 h-5 text-red-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            )}
            {isReject
              ? t("smartCashier.rejectTransaction", "ปฏิเสธรายการ")
              : t("smartCashier.cancelTransaction", "ยกเลิกรายการ")}
          </DialogTitle>
          <DialogDescription>
            {isReject
              ? t(
                  "smartCashier.rejectDescription",
                  "รายการนี้จะถูกปฏิเสธและไม่สามารถดำเนินการต่อได้"
                )
              : t(
                  "smartCashier.cancelDescription",
                  "รายการนี้จะถูกยกเลิก"
                )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Transaction Summary */}
          <div className="p-4 rounded-lg bg-muted/50 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {t("smartCashier.amount", "จำนวนเงิน")}
              </span>
              <span className="font-mono font-semibold text-lg">
                ฿{transaction.amount.toLocaleString()}
              </span>
            </div>
            {transaction.memberName && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {t("smartCashier.member", "สมาชิก")}
                </span>
                <span className="text-sm">{transaction.memberName}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {t("smartCashier.currentStatus", "สถานะปัจจุบัน")}
              </span>
              <Badge
                variant="outline"
                className={cn(
                  transaction.status === "Pending" &&
                    "border-amber-500 text-amber-600",
                  transaction.status === "Processing" &&
                    "border-blue-500 text-blue-600"
                )}
              >
                {transaction.status}
              </Badge>
            </div>
          </div>

          {/* Credit Refund Warning */}
          {willRefundCredit && (
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 flex items-start gap-2">
              <Undo2 className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-blue-700 dark:text-blue-400">
                  {t("smartCashier.creditWillRefund", "เครดิตจะถูกคืน")}
                </p>
                <p className="text-blue-600 dark:text-blue-300 text-xs">
                  {t(
                    "smartCashier.creditRefundNote",
                    "เครดิตที่ตัดไปแล้วจะถูกคืนกลับให้สมาชิกโดยอัตโนมัติ"
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Reason Input */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="font-medium">
              {t("smartCashier.reason", "เหตุผล")}{" "}
              <span className="text-red-500">*</span>
            </Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {presetReasons.map((r) => (
                <Button
                  key={r}
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-7 text-xs",
                    reason === r &&
                      "bg-primary text-primary-foreground border-primary"
                  )}
                  onClick={() => setReason(r)}
                >
                  {r}
                </Button>
              ))}
            </div>
            <Textarea
              id="reason"
              placeholder={t(
                "smartCashier.enterReason",
                "กรุณาระบุเหตุผล หรือเลือกจากตัวเลือกด้านบน"
              )}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (error) setError("");
              }}
              rows={3}
              className={cn(error && "border-red-500")}
            />
            {error && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {error}
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t("common.cancel", "ยกเลิก")}
          </Button>
          <Button
            variant={isReject ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={isLoading || !reason.trim()}
            className={!isReject ? "bg-amber-600 hover:bg-amber-700" : ""}
          >
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isReject
              ? t("smartCashier.confirmReject", "ยืนยันปฏิเสธ")
              : t("smartCashier.confirmCancel", "ยืนยันยกเลิก")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReasonDialog;




